import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import SharedTagPill from '../general/TagPill';
import { useTagConfig } from '../../hooks/TagConfigContainer';
import { breakpoint_small } from '../../constants/breakpoints';

interface SessionTagsProps {
    tags?: string[];
    featureFlag?: string;
    className?: string;
    showVfyTags?: boolean;
}

// Tags that should be grouped with counts when they appear multiple times
const GROUPABLE_TAGS = [
    'ACTION_COMPLETED',
    'APPOINTMENT_RESCHEDULED',
    'APPOINTMENT_CANCELLED',
    'CHECK_NEXT_APPOINTMENT_COMPLETE'
];

interface GroupedTag {
    tag: string;
    count: number;
}

const groupTagsWithCounts = (tags: string[]): GroupedTag[] => {
    const tagCounts = tags.reduce((acc, tag) => {
        acc[tag] = (acc[tag] || 0) + 1;
        return acc;
    }, {} as Record<string, number>);

    return Object.entries(tagCounts).map(([tag, count]) => ({ tag, count }));
};

const SessionTags: React.FC<SessionTagsProps> = ({ tags, featureFlag, className, showVfyTags }) => {
    const { sortTagsByPriority } = useTagConfig();

    if (!tags || tags.length === 0) {
        return null;
    }

    const groupedTags = useMemo(() => {
        let filteredTags = tags;
        if (!showVfyTags) {
            filteredTags = filteredTags.filter(tag => !tag?.toLowerCase().startsWith('vfy_'));
        }

        // Group tags and count occurrences
        const grouped = groupTagsWithCounts(filteredTags);

        // Sort the unique tags
        const uniqueTags = grouped.map(g => g.tag);
        const sortedUniqueTags = sortTagsByPriority(uniqueTags, featureFlag);

        // Return grouped tags in sorted order
        return sortedUniqueTags.map(tag => {
            const found = grouped.find(g => g.tag === tag);
            return found || { tag, count: 1 };
        });
    }, [tags, featureFlag, showVfyTags]);

    return (
        <TagsContainer className={className}>
            {groupedTags.map(({ tag, count }) => (
                <SharedTagPill
                    key={tag}
                    tag={tag}
                    featureFlag={featureFlag}
                    count={GROUPABLE_TAGS.includes(tag) ? count : undefined}
                />
            ))}
        </TagsContainer>
    );
};

const TagsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    align-items: center;
    margin-left: 8px;
    ${breakpoint_small} {
        margin-left: 0;
        margin-top: 4px;
    }
`;

export default SessionTags;
